import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { createCategory, listCategories } from "./api.js";
import { parentFolderOptions } from "./folderOptions.js";

/**
 * Create-a-category modal.
 *
 * Shared standalone component — the same dialog mounts on the
 * `DocumentationIndex` empty state AND the `DocumentationSidebar` left-menu
 * action, exactly like `EditCategoryDialog` is shared between the sidebar
 * and `CategoriesManagePage`. There is intentionally no second copy of
 * this form: a duplicated modal is the dead code this extraction exists
 * to prevent.
 *
 * Categories are the documentation "folders" — the buckets the type
 * grid and the customer KB sidebar group docs by (e.g. a "Video
 * Trainings" folder you then upload videos into via the existing
 * upload page). The Rails Sluggable concern derives the slug from the
 * name. The caller owns cache invalidation through `onCreated` so each
 * surface can refresh exactly the queries it shows.
 *
 * SUB-FOLDERS (2026-09-23): the "Inside" picker files the new folder under
 * a top-level one ("Data Sheets" → "USN"). Nesting is ONE level: only
 * top-level folders are offered, and portal-api refuses anything deeper.
 * `parentId` pre-selects it — the sidebar's "+" on a folder row opens this
 * dialog already pointed at that folder.
 */
export function NewCategoryModal({
  open,
  onClose,
  onCreated,
  parentId = null,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  parentId?: number | null;
}): JSX.Element {
  const [name, setName] = useState("");
  const [parent, setParent] = useState(parentId != null ? String(parentId) : "");
  useEffect(() => {
    if (open) setParent(parentId != null ? String(parentId) : "");
  }, [open, parentId]);
  const { data: cats } = useQuery({ queryKey: ["docs", "categories"], queryFn: listCategories, enabled: open });
  const parents = useMemo(() => parentFolderOptions(cats?.data ?? []), [cats]);
  const isSub = parent !== "";
  const [visible, setVisible] = useState(true);
  // Reseller visibility starts OFF, matching the server default. A new
  // folder is not partner material until someone says it is.
  const [resellerVisible, setResellerVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createCategory({
        document_category: {
          name: name.trim(),
          parent_id: parent ? Number(parent) : null,
          visible_in_customer_portal: visible,
          visible_in_reseller_portal: resellerVisible,
        },
      }),
    onSuccess: () => {
      setName("");
      setParent("");
      setVisible(true);
      setError(null);
      onCreated();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Could not create category");
    },
  });

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography component="span" sx={{ display: "block", fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}>
          {isSub ? "New sub-folder" : "New category"}
        </Typography>
        <Typography component="span" sx={{ display: "block", fontSize: 12, fontWeight: 400, color: "text.disabled" }}>
          Buckets the type-grid and the customer KB sidebar use to group docs.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{ display: "grid", gap: 2, pt: 1 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            mutation.mutate();
          }}
        >
          <Box sx={{ display: "grid", gap: 0.75 }}>
            <TextField
              id="new-category-name"
              label="Name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Onboarding"
              inputProps={{ required: true }}
              fullWidth
            />
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              The slug auto-derives from the name (lowercase, dashes).
            </Typography>
          </Box>

          <TextField
            select
            label="Inside"
            value={parent}
            onChange={(e) => setParent(e.target.value)}
            fullWidth
            helperText="Pick a folder to make this a sub-folder of it. Sub-folders go two levels deep."
            inputProps={{ "data-testid": "new-category-parent" }}
          >
            <MenuItem value="">Top level (a new folder)</MenuItem>
            {parents.map((p) => (
              <MenuItem key={p.id} value={String(p.id)} sx={p.depth > 0 ? { pl: 2 + p.depth * 2 } : {}}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>

          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5),
              p: 1.5,
              fontSize: 14,
            }}
          >
            <Box
              component="input"
              type="checkbox"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
              sx={{ mt: 0.25 }}
            />
            <Box component="span">
              <Box component="span" sx={{ fontWeight: 500 }}>
                Visible in customer portal
              </Box>
              <Box component="span" sx={{ display: "block", fontSize: 12, color: "text.disabled" }}>
                When off, the category and its published docs are hidden from
                the customer KB sidebar. You can flip this any time from the
                card's eye toggle.
              </Box>
            </Box>
          </Box>

          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5),
              p: 1.5,
              fontSize: 14,
            }}
          >
            <Box
              component="input"
              type="checkbox"
              checked={resellerVisible}
              onChange={(e) => setResellerVisible(e.target.checked)}
              data-testid="new-category-visible-resellers"
              sx={{ mt: 0.25 }}
            />
            <Box component="span">
              <Box component="span" sx={{ fontWeight: 500 }}>
                Visible in reseller portal
              </Box>
              <Box component="span" sx={{ display: "block", fontSize: 12, color: "text.disabled" }}>
                Independent of the customer switch. When on, XOP Reseller partners
                can read this folder&rsquo;s published docs. Off by default.
              </Box>
            </Box>
          </Box>

          {error && (
            <Box
              sx={{
                borderRadius: 1,
                border: 1,
                borderColor: "error.main",
                bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                px: 1.5,
                py: 1,
                fontSize: 12,
                color: "error.main",
              }}
            >
              {error}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              borderTop: 1,
              borderColor: "divider",
              mx: -3,
              mb: -2,
              px: 3,
              py: 1.5,
            }}
          >
            <Button type="button" variant="text" color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!name.trim() || mutation.isPending}>
              {mutation.isPending ? "Creating…" : isSub ? "Create sub-folder" : "Create category"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
